<template>
  <div class="h-screen flex md:flex-row bg-zinc-950 overflow-hidden font-light selection:bg-white selection:text-black">

    <div class="bg-zinc-950 flex flex-col justify-center items-center w-full md:w-1/2 p-8 sm:p-12 md:p-16 overflow-y-auto no-scrollbar border-r border-zinc-900/60 relative">
      <div class="absolute top-1/4 left-1/2 -translate-x-1/2 w-[300px] h-[300px] bg-zinc-900/20 rounded-full blur-[100px] pointer-events-none"></div>

      <div class="w-full max-w-md mb-8 text-left">
        <span class="text-xs font-serif italic text-[#D5CCBC] tracking-wide">BookMySalon PRO</span>
      </div>

      <!-- RESET PASSWORD FORM -->
      <form v-if="!resetSuccess" @submit.prevent="handleResetPassword" class="w-full max-w-md space-y-6 text-left relative z-10">
        <div class="space-y-1">
          <h2 class="text-xl font-medium text-white tracking-tight">Nouveau Mot de Passe</h2>
          <p class="text-xs text-zinc-500 font-light">Créez un mot de passe sécurisé pour votre compte.</p>
        </div>

        <div class="space-y-4 pt-4">
          <div class="space-y-1.5">
            <label class="block text-[10px] uppercase tracking-widest text-zinc-500 font-medium" for="new-password">Nouveau mot de passe</label>
            <input
              v-model="formData.newPassword"
              id="new-password"
              placeholder="••••••••"
              required
              type="password"
              class="w-full px-4 py-3 rounded-xl border border-zinc-900 bg-zinc-900/40 text-white placeholder-zinc-700 focus:outline-none focus:border-zinc-700 focus:bg-zinc-900/80 text-sm tracking-wide transition-all duration-300" />

            <!-- Password Strength Indicator -->
            <div v-if="formData.newPassword" class="space-y-2 pt-2">
              <!-- Progress Bar -->
              <div class="flex gap-1">
                <div
                  v-for="i in 4"
                  :key="i"
                  :class="[
                    'h-1 flex-1 rounded-full transition-all duration-300',
                    i <= passwordStrength.level
                      ? passwordStrength.level === 1 ? 'bg-red-500'
                      : passwordStrength.level === 2 ? 'bg-orange-500'
                      : passwordStrength.level === 3 ? 'bg-yellow-500'
                      : 'bg-emerald-500'
                      : 'bg-zinc-800'
                  ]" />
              </div>

              <!-- Criteria List -->
              <div class="space-y-1">
                <div class="flex items-center gap-2">
                  <span :class="['text-[10px]', passwordStrength.hasMinLength ? 'text-emerald-400' : 'text-zinc-500']">
                    {{ passwordStrength.hasMinLength ? '✓' : '○' }} Minimum 8 caractères
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <span :class="['text-[10px]', passwordStrength.hasUpperCase ? 'text-emerald-400' : 'text-zinc-500']">
                    {{ passwordStrength.hasUpperCase ? '✓' : '○' }} Une majuscule
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <span :class="['text-[10px]', passwordStrength.hasNumber ? 'text-emerald-400' : 'text-zinc-500']">
                    {{ passwordStrength.hasNumber ? '✓' : '○' }} Un chiffre
                  </span>
                </div>
                <div class="flex items-center gap-2">
                  <span :class="['text-[10px]', passwordStrength.hasSpecialChar ? 'text-emerald-400' : 'text-zinc-500']">
                    {{ passwordStrength.hasSpecialChar ? '✓' : '○' }} Un caractère spécial (!@#$%^&*)
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="space-y-1.5">
            <label class="block text-[10px] uppercase tracking-widest text-zinc-500 font-medium" for="confirm-password">Confirmer le mot de passe</label>
            <input
              v-model="formData.confirmPassword"
              id="confirm-password"
              placeholder="••••••••"
              required
              type="password"
              class="w-full px-4 py-3 rounded-xl border border-zinc-900 bg-zinc-900/40 text-white placeholder-zinc-700 focus:outline-none focus:border-zinc-700 focus:bg-zinc-900/80 text-sm tracking-wide transition-all duration-300" />

            <!-- Match indicator -->
            <div v-if="formData.confirmPassword" class="flex items-center gap-2 pt-1">
              <span :class="['text-[10px]', passwordsMatch ? 'text-emerald-400' : 'text-red-400']">
                {{ passwordsMatch ? '✓ Les mots de passe correspondent' : '✗ Les mots de passe ne correspondent pas' }}
              </span>
            </div>
          </div>
        </div>

        <div class="space-y-3 pt-4">
          <button
            :disabled="isLoading || !isFormValid"
            class="w-full py-3.5 rounded-xl bg-white hover:bg-[#D5CCBC] text-black font-semibold text-xs uppercase tracking-wider transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl shadow-white/5 active:scale-[0.99]"
            type="submit">
            {{ isLoading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe' }}
          </button>

          <button
            class="w-full py-3.5 rounded-xl bg-zinc-900/30 text-zinc-400 hover:text-white text-xs uppercase tracking-wider font-medium border border-zinc-800/80 transition-all duration-300"
            type="button"
            @click="goToLogin">
            Retourner à la connexion
          </button>
        </div>
      </form>

      <!-- SUCCESS MESSAGE -->
      <div v-else class="w-full max-w-md space-y-6 text-left relative z-10">
        <div class="space-y-1">
          <div class="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg class="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h2 class="text-xl font-medium text-white tracking-tight text-center">Mot de passe réinitialisé</h2>
          <p class="text-xs text-zinc-500 font-light text-center">Votre mot de passe a été mis à jour avec succès.</p>
        </div>

        <button
          @click="goToLogin"
          class="w-full py-3.5 rounded-xl bg-white hover:bg-[#D5CCBC] text-black font-semibold text-xs uppercase tracking-wider transition-all duration-300 shadow-xl shadow-white/5 active:scale-[0.99]">
          Se connecter
        </button>
      </div>

    </div>

    <!-- Right Side Hero section -->
    <div class="hidden md:block w-1/2 h-full relative bg-zinc-900">
      <div class="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/40 to-transparent z-10 w-1/3"></div>
      <div class="absolute inset-0 bg-zinc-950/10 z-10"></div>

      <div class="absolute bottom-12 left-12 z-20 max-w-xs space-y-2 p-6 bg-zinc-950/40 backdrop-blur-md rounded-2xl border border-zinc-900 text-left">
        <p class="text-xs text-[#D5CCBC] font-serif italic">BookMySalon Pro</p>
        <p class="text-[11px] text-zinc-400 font-light leading-relaxed">"Sécurisez votre accès avec un mot de passe robuste et unique."</p>
      </div>

      <img
        alt="Professional beauty salon session"
        class="object-cover w-full h-full filter grayscale contrast-105 brightness-[0.85]"
        src="https://img.freepik.com/free-photo/bride-getting-makeup-done-medium-shot_23-2149860778.jpg?uid=R141327891&ga=GA1.1.223045777.1725963888&semt=ais_hybrid&w=740"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import api from '@/api'

const router = useRouter()
const route = useRoute()

const isLoading = ref(false)
const resetSuccess = ref(false)

const formData = reactive({
  newPassword: '',
  confirmPassword: ''
})

// Get token from URL
const token = computed(() => route.params.token)

// Computed: Password strength
const passwordStrength = computed(() => {
  const pwd = formData.newPassword

  const hasMinLength = pwd.length >= 8
  const hasUpperCase = /[A-Z]/.test(pwd)
  const hasNumber = /[0-9]/.test(pwd)
const hasSpecialChar = /[!@#$%^&*()_+={}[\]|;':"\\,.<>?/-]/.test(pwd)  // Calculate level (1-4)
  let level = 0
  if (hasMinLength) level++
  if (hasUpperCase) level++
  if (hasNumber) level++
  if (hasSpecialChar) level++

  return {
    level,
    hasMinLength,
    hasUpperCase,
    hasNumber,
    hasSpecialChar
  }
})

// Computed: Passwords match
const passwordsMatch = computed(() => {
  return formData.newPassword === formData.confirmPassword && formData.confirmPassword.length > 0
})

// Computed: Form is valid
const isFormValid = computed(() => {
  return passwordStrength.value.level === 4 && passwordsMatch.value
})

// Handle Reset Password
async function handleResetPassword() {
  if (isLoading.value) return

  // Validation
  if (!isFormValid.value) {
    toast.error('Veuillez respecter tous les critères de sécurité du mot de passe.', {
      position: 'top-right',
      autoClose: 3000,
      theme: 'dark'
    })
    return
  }

  try {
    isLoading.value = true

    const response = await api.auth.updatePassword(token.value, {
      newPassword: formData.newPassword,
      confirmPassword: formData.confirmPassword
    })

    if (response.data.success) {
      resetSuccess.value = true

      toast.success('Mot de passe réinitialisé avec succès !', {
        position: 'top-right',
        autoClose: 3000,
        theme: 'dark'
      })

      // Redirection automatique après 3 secondes
      setTimeout(() => {
        router.push('/login')
      }, 3000)
    }
  } catch (error) {
    console.error('Erreur de réinitialisation:', error)

    const errorMessage = error.response?.data?.message ||
                        error.response?.data?.error ||
                        'Erreur lors de la réinitialisation. Le lien a peut-être expiré.'

    toast.error(errorMessage, {
      position: 'top-right',
      autoClose: 5000,
      theme: 'dark'
    })
  } finally {
    isLoading.value = false
  }
}

// Go to login
function goToLogin() {
  router.push('/login')
}

// Check token validity on mount
onMounted(() => {
  if (!token.value) {
    toast.error('Lien de réinitialisation invalide.', {
      position: 'top-right',
      autoClose: 3000,
      theme: 'dark'
    })
    setTimeout(() => {
      router.push('/login')
    }, 2000)
  }
})
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
